import {
  canLoadMonetagInPage,
  canLoadMonetagVignette,
  getLoadedMonetagZones,
  isMonetagRuntimeAllowed,
  MONETAG_LIMITS,
  MONETAG_STORAGE_KEYS,
  MONETAG_ZONES,
  normalizeMonetagRoute,
  requestMonetagInPagePush,
  requestMonetagPushNotification,
  requestMonetagVignetteBanner,
  requestMonetagPopunder,
  safeLoadMonetagAd
} from "../lib/ads/monetagAds";

const isBrowser = typeof window !== "undefined";

export const AD_STORAGE_KEYS = {
  consent: "techniahq.ads.consent",
  sessionStart: "techniahq.ads.sessionStart",
  pageViews: "techniahq.ads.pageViews",
  inPagePushShown: MONETAG_STORAGE_KEYS.inPageSessionLoaded,
  routeFormats: "techniahq.ads.routeFormats",
  vignetteShownAt: MONETAG_STORAGE_KEYS.vignetteLastShown,
  pushShownAt: MONETAG_STORAGE_KEYS.pushLastShown,
  onclickShownAt: MONETAG_STORAGE_KEYS.onclickLastShown,
  loadedZones: MONETAG_STORAGE_KEYS.loadedZones,
  closedAds: "techniahq.ads.closedAds"
};

export const AD_FORMATS = {
  inPagePush: "inPagePush",
  articleNative: "articleNative",
  sidebarNative: "sidebarNative",
  vignetteBanner: "vignetteBanner",
  pushNotification: "pushNotification",
  popunder: "popunder"
};

export const adsConfig = {
  enabled: process.env.REACT_APP_ADS_ENABLED !== "false" && process.env.NODE_ENV === "production",
  debug: process.env.REACT_APP_ADS_DEBUG === "true",
  consent: {
    required: false,
    storageKey: AD_STORAGE_KEYS.consent,
    version: "2026-07-monetag-controlled-v3"
  },
  limits: {
    maxActiveFormatsPerPage: MONETAG_LIMITS.desktopMaxGlobalTags,
    maxInPagePushPerSession: 1,
    maxVignettePerHours: 24,
    maxPushNotificationPerHours: 24,
    maxPopunderPerHours: 24
  },
  delays: {
    inPagePushMinMs: MONETAG_LIMITS.globalDelayMinMs,
    inPagePushMaxMs: MONETAG_LIMITS.globalDelayMaxMs,
    routeIdleMs: 700
  },
  thresholds: {
    articleScrollDepth: 10,
    mobileConnectionAvoidList: ["slow-2g", "2g"]
  },
  routes: {
    deniedExact: [
      "/privacy",
      "/privacy-policy",
      "/terms",
      "/terms-of-use",
      "/cookie-policy",
      "/cookies",
      "/contact",
      "/partnerships",
      "/advertise",
      "/editorial-policy",
      "/about-techniahq",
      "/login",
      "/admin",
      "/dashboard",
      "/account",
      "/profile",
      "/settings",
      "/manager",
      "/private"
    ],
    deniedPrefixes: [
      "/admin",
      "/dashboard",
      "/account",
      "/profile",
      "/settings",
      "/manager",
      "/private",
      "/login",
      "/register",
      "/checkout"
    ],
    editorialPrefixes: [
      "/ai-news",
      "/ai",
      "/robotics-news",
      "/robotics",
      "/robot-types"
    ],
    guideExact: [
      "/",
      "/robots",
      "/robotics",
      "/robot-types",
      "/robot-sensors",
      "/robot-components",
      "/robotics-software",
      "/robotics-topics",
      "/robotics-companies",
      "/robotics-events",
      "/viral-robotics-topics",
      "/robotics-search-topics",
      "/robotics-data-collection",
      "/robot-management-software",
      "/robotic-arm-joint-components",
      "/nuclear-robots",
      "/construction-robotics",
      "/robotics-conferences",
      "/entertainment-robotics",
      "/micro-robotics",
      "/drones-plus-robotics",
      "/humanoid-robots/united-states",
      "/humanoid-robots/france",
      "/humanoid-robots/japan",
      "/humanoid-robots/china",
    ]
  },
  monetag: {
    zones: MONETAG_ZONES,
    limits: MONETAG_LIMITS,
    storageKeys: MONETAG_STORAGE_KEYS
  },
  formats: {
    inPagePush: {
      id: "techniahq-monetag-in-page-push",
      enabled: false,
      label: "Monetag In-Page Push rotation",
      src: "central-monetag-loader",
      dataset: { zone: "rotation" },
      cfasync: false,
      async: true,
      requiresConsent: false,
      allowedOnMobile: true,
      intrusive: false,
      visiblePlacement: true,
      frequency: "once-per-session"
    },
    articleNative: {
      id: "techniahq-article-native-slot",
      label: "Article ad slot",
      requiresConsent: false,
      allowedOnMobile: true,
      intrusive: false,
      visiblePlacement: true,
      frequency: "per-visible-slot"
    },
    sidebarNative: {
      id: "techniahq-sidebar-native-slot",
      label: "Desktop sidebar ad slot",
      requiresConsent: false,
      allowedOnMobile: false,
      intrusive: false,
      visiblePlacement: true,
      frequency: "per-visible-slot"
    },
    vignetteBanner: {
      id: "techniahq-monetag-vignette-banner",
      enabled: false,
      label: "Monetag Vignette Banner rotation",
      src: "central-monetag-loader",
      dataset: { zone: "rotation" },
      cfasync: false,
      async: true,
      requiresConsent: false,
      allowedOnMobile: true,
      intrusive: true,
      visiblePlacement: false,
      frequency: "once-per-24-hours"
    },
    pushNotification: {
      id: "techniahq-monetag-push-notification",
      enabled: false,
      label: "Monetag Push Notification 11248779",
      src: "central-monetag-loader",
      dataset: { zone: "11248779" },
      cfasync: false,
      async: true,
      requiresConsent: false,
      allowedOnMobile: false,
      intrusive: true,
      visiblePlacement: false,
      frequency: "once-per-24-hours"
    },
    popunder: {
      id: "techniahq-monetag-popunder-disabled",
      label: "Monetag OnClick / Popunder 11248769 disabled by default",
      src: "central-monetag-loader",
      dataset: { zone: "11248769" },
      cfasync: false,
      async: true,
      enabled: false,
      requiresConsent: false,
      allowedOnMobile: false,
      intrusive: true,
      visiblePlacement: false,
      frequency: "disabled"
    }
  }
};

function logDebug(...args) {
  if (adsConfig.debug && typeof console !== "undefined") {
    console.debug("[TechniaHQRobot Ads]", ...args);
  }
}

export function normalizeRoute(pathname = "/") {
  return normalizeMonetagRoute(pathname);
}

export function readStorage(key, fallback = null, storage) {
  if (!isBrowser) return fallback;
  const targetStorage = storage || window.localStorage;
  try {
    const value = targetStorage.getItem(key);
    return value === null ? fallback : value;
  } catch (_) {
    return fallback;
  }
}

export function writeStorage(key, value, storage) {
  if (!isBrowser) return;
  const targetStorage = storage || window.localStorage;
  try {
    targetStorage.setItem(key, String(value));
  } catch (_) {
    // Storage can fail in private browsing. Ads fail closed.
  }
}

export function readJsonStorage(key, fallback = {}, storage) {
  const raw = readStorage(key, null, storage);
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch (_) {
    return fallback;
  }
}

export function writeJsonStorage(key, value, storage) {
  writeStorage(key, JSON.stringify(value), storage);
}

export function adsRuntimeEnabled() {
  if (!adsConfig.enabled || !isBrowser) return false;
  return isMonetagRuntimeAllowed(window.location.pathname);
}

export function getConsentState() {
  const fallback = { accepted: false, decided: false, version: adsConfig.consent.version };
  if (!isBrowser) return fallback;
  if (!adsConfig.consent.required) return { accepted: true, decided: true, version: adsConfig.consent.version };
  const stored = readJsonStorage(adsConfig.consent.storageKey, fallback, window.localStorage);
  if (stored?.version !== adsConfig.consent.version) return fallback;
  return {
    accepted: stored.accepted === true,
    decided: stored.decided === true,
    version: stored.version
  };
}

export function setConsentState(accepted) {
  if (!isBrowser) return;
  writeJsonStorage(adsConfig.consent.storageKey, {
    accepted: accepted === true,
    decided: true,
    version: adsConfig.consent.version,
    updatedAt: Date.now()
  }, window.localStorage);
}

export function isDeniedRoute(pathname) {
  const route = normalizeRoute(pathname);
  const { deniedExact, deniedPrefixes } = adsConfig.routes;
  return deniedExact.includes(route) || deniedPrefixes.some((prefix) => route === prefix || route.startsWith(`${prefix}/`));
}

export function isEditorialRoute(pathname) {
  const route = normalizeRoute(pathname);
  return adsConfig.routes.editorialPrefixes.some((prefix) => route === prefix || route.startsWith(`${prefix}/`));
}

export function isGuideRoute(pathname) {
  const route = normalizeRoute(pathname);
  return adsConfig.routes.guideExact.includes(route) || isEditorialRoute(route);
}

export function isArticleRoute(pathname) {
  const route = normalizeRoute(pathname);
}

export function isMobileDevice() {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "");
}

export function isSlowMobileConnection() {
  if (typeof navigator === "undefined") return false;
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!connection) return false;
  return adsConfig.thresholds.mobileConnectionAvoidList.includes(connection.effectiveType);
}

export function getScrollDepth() {
  if (!isBrowser || typeof document === "undefined") return 0;
  const doc = document.documentElement;
  const scrollTop = window.scrollY || doc.scrollTop || 0;
  const scrollable = Math.max(1, doc.scrollHeight - window.innerHeight);
  return Math.min(100, Math.round((scrollTop / scrollable) * 100));
}

export function ensureAdSession() {
  if (!isBrowser) return;
  const existing = readStorage(AD_STORAGE_KEYS.sessionStart, null, window.sessionStorage);
  if (!existing) writeStorage(AD_STORAGE_KEYS.sessionStart, Date.now(), window.sessionStorage);
  const current = Number(readStorage(AD_STORAGE_KEYS.pageViews, 0, window.sessionStorage)) || 0;
  writeStorage(AD_STORAGE_KEYS.pageViews, current + 1, window.sessionStorage);
  getLoadedMonetagZones();
}

export function getSessionState() {
  if (!isBrowser) {
    return {
      now: Date.now(),
      sessionStart: Date.now(),
      pageViews: 0,
      timeOnSiteMs: 0,
      consent: getConsentState(),
      isMobile: false,
      slowConnection: false,
      routeFormats: {}
    };
  }
  const now = Date.now();
  const sessionStart = Number(readStorage(AD_STORAGE_KEYS.sessionStart, now, window.sessionStorage)) || now;
  const pageViews = Number(readStorage(AD_STORAGE_KEYS.pageViews, 0, window.sessionStorage)) || 0;
  return {
    now,
    sessionStart,
    pageViews,
    timeOnSiteMs: now - sessionStart,
    consent: getConsentState(),
    isMobile: isMobileDevice(),
    slowConnection: isSlowMobileConnection(),
    routeFormats: readJsonStorage(AD_STORAGE_KEYS.routeFormats, {}, window.sessionStorage)
  };
}

export function markFormatActive(format, route) {
  if (!isBrowser) return;
  const normalizedRoute = normalizeRoute(route);
  const routeFormats = readJsonStorage(AD_STORAGE_KEYS.routeFormats, {}, window.sessionStorage);
  const current = new Set(routeFormats[normalizedRoute] || []);
  current.add(format);
  routeFormats[normalizedRoute] = Array.from(current);
  writeJsonStorage(AD_STORAGE_KEYS.routeFormats, routeFormats, window.sessionStorage);
}

export function countActiveFormatsOnRoute(route) {
  if (!isBrowser) return 0;
  const routeFormats = readJsonStorage(AD_STORAGE_KEYS.routeFormats, {}, window.sessionStorage);
  return (routeFormats[normalizeRoute(route)] || []).length;
}

function formatFrequencyAllowed(format, route) {
  if (!isBrowser) return false;
  switch (format) {
    case AD_FORMATS.inPagePush:
      return canLoadMonetagInPage(route);
    case AD_FORMATS.vignetteBanner:
      return canLoadMonetagVignette(route);
    case AD_FORMATS.pushNotification:
      return true;
    case AD_FORMATS.popunder:
      return false;
    case AD_FORMATS.articleNative:
    case AD_FORMATS.sidebarNative:
      return true;
    default:
      return false;
  }
}

function engagementAllowed(format, route, state) {
  const routeIsArticle = isArticleRoute(route);
  switch (format) {
    case AD_FORMATS.inPagePush:
      return (routeIsArticle || isGuideRoute(route)) && state.scrollDepth >= adsConfig.thresholds.articleScrollDepth;
    case AD_FORMATS.articleNative:
      return routeIsArticle;
    case AD_FORMATS.sidebarNative:
      return !state.isMobile && routeIsArticle;
    case AD_FORMATS.vignetteBanner:
      return routeIsArticle || isGuideRoute(route);
    case AD_FORMATS.pushNotification:
      return !state.isMobile && (routeIsArticle || isGuideRoute(route));
    case AD_FORMATS.popunder:
      return false;
    default:
      return false;
  }
}

export function canShowAd(format, route, userState = {}) {
  if (!adsRuntimeEnabled()) return false;
  const routePath = normalizeRoute(route);
  if (isDeniedRoute(routePath)) return false;

  const formatConfig = adsConfig.formats[format];
  if (!formatConfig || formatConfig.enabled === false) return false;

  const state = { ...getSessionState(), ...userState };

  if (formatConfig.requiresConsent && !state.consent.accepted) return false;
  if (state.slowConnection) return false;
  if (state.isMobile && formatConfig.allowedOnMobile === false) return false;
  if (countActiveFormatsOnRoute(routePath) >= adsConfig.limits.maxActiveFormatsPerPage) return false;
  if (!formatFrequencyAllowed(format, routePath)) return false;
  if (!engagementAllowed(format, routePath, state)) return false;

  return true;
}

export function runWhenIdle(callback, fallbackMs = 1200) {
  if (!isBrowser) return undefined;
  if ("requestIdleCallback" in window) {
    return window.requestIdleCallback(callback, { timeout: fallbackMs + 2500 });
  }
  return window.setTimeout(callback, fallbackMs);
}

export function cancelIdleTask(taskId) {
  if (!isBrowser || taskId === undefined) return;
  if ("cancelIdleCallback" in window) {
    window.cancelIdleCallback(taskId);
    return;
  }
  window.clearTimeout(taskId);
}

export function loadAdScript({ src, dataset = {}, async = true, cfasync = false, format = AD_FORMATS.inPagePush, route = window.location.pathname, placement = "slot" }) {
  const zoneId = dataset?.zone;
  if (!zoneId || zoneId === "rotation") {
    if (format === AD_FORMATS.vignetteBanner) return requestMonetagVignetteBanner(route, { placement });
    if (format === AD_FORMATS.pushNotification) return requestMonetagPushNotification(route, { placement });
    if (format === AD_FORMATS.popunder) return requestMonetagPopunder(route, { placement });
    return requestMonetagInPagePush(route, { placement });
  }
  return safeLoadMonetagAd({ zoneId, src, format, route, placement, async, cfasync });
}

export async function loadFormat(format, route, userState = {}) {
  const formatConfig = adsConfig.formats[format];
  if (!formatConfig || !canShowAd(format, route, userState)) {
    return { skipped: true, format };
  }

  if (format === AD_FORMATS.inPagePush) {
    const result = await requestMonetagInPagePush(route, { placement: userState.placement || "visible-slot" });
    markFormatActive(format, route);
    logDebug("activated format", format, route, result);
    return result;
  }

  if (format === AD_FORMATS.vignetteBanner) {
    const result = await requestMonetagVignetteBanner(route, { placement: userState.placement || "global" });
    markFormatActive(format, route);
    logDebug("activated format", format, route, result);
    return result;
  }

  if (format === AD_FORMATS.pushNotification) {
    const result = await requestMonetagPushNotification(route, { placement: userState.placement || "global-delayed-push" });
    markFormatActive(format, route);
    logDebug("activated format", format, route, result);
    return result;
  }

  if (format === AD_FORMATS.popunder) {
    return { skipped: true, format, reason: "popunder-disabled-by-default" };
  }

  markFormatActive(format, route);
  return { rendered: true, format };
}

export function markAdClosed(format = "unknown") {
  if (!isBrowser) return;
  try {
    const raw = window.localStorage.getItem(AD_STORAGE_KEYS.closedAds);
    const current = raw ? JSON.parse(raw) : {};
    current[format] = { closedAt: Date.now() };
    window.localStorage.setItem(AD_STORAGE_KEYS.closedAds, JSON.stringify(current));
  } catch (_) {
    // fail closed
  }
}
